import React, { useState, useEffect } from "react";
import Input from "../input";
import Icon from "../../assets/icons";
import FIlterSelect from "../filter-select";
import { Plus } from "lucide-react";
import {
  I_Requisition,
  I_RequisitionItem,
} from "../../pages/requisition/types";

interface SalesDetailsProps {
  control?: any;
  setValue?: any;
  errors: any;
  reqs?: I_Requisition | null;
  initialProducts?: Product[];
  onProductsChange?: (products: Product[]) => void;
  // Add optional product catalog for prices
  productCatalog?: Record<string, ProductCatalogItem>;
}

interface Product {
  id: number;
  name: string;
  quantity: number;
  price: number;
  _id: string;
}

interface ProductCatalogItem {
  name: string;
  price: number;
  // Add other product details as needed
}

const AddNewProduct: React.FC<SalesDetailsProps> = ({
  initialProducts,
  onProductsChange,
  reqs,
  productCatalog, // Optional product catalog
}) => {
  // Use initialProducts if provided, otherwise use default
  const [products, setProducts] = useState<Product[]>(
    initialProducts && initialProducts.length > 0
      ? initialProducts
      : [{ id: Date.now(), name: "", price: 0, _id: "", quantity: 0 }]
  );

  // Enhanced mapping to include price from requisition or catalog
  const productMap = React.useMemo(() => {
    const map: Record<string, { name: string; quantity: number; price?: number }> = {};

    // Check if reqs exists and has Items array
    if (reqs && reqs.Item && Array.isArray(reqs.Item)) {
      reqs.Item.forEach((item: I_RequisitionItem) => {
        // Handle the nested _id structure as shown in the interface
        if (item._id && typeof item._id === "object" && item._id._id) {
          const productId = item._id._id;
          
          map[productId] = {
            name: item.name || "",
            quantity: item.quantity || 0,
            // Try to get price from requisition item first
            price: item.unitPrice || undefined,
          };

          // If no price in requisition, try to get from product catalog
          if (!map[productId].price && productCatalog && productCatalog[productId]) {
            map[productId].price = productCatalog[productId].price;
          }
        }
      });
    }
    
    return map;
  }, [reqs, productCatalog]);

  const RequisitionOptions = React.useMemo(() => {
    if (reqs && reqs.Item && Array.isArray(reqs.Item)) {
      return reqs.Item
        .map((item: I_RequisitionItem) => {
          if (item._id && typeof item._id === "object" && item._id._id) {
            return {
              value: item._id._id,
              label: item.name || "Unnamed Item",
            };
          }
          return null;
        })
        .filter((opt): opt is { value: string; label: string } => opt !== null); 
    }
    return [];
  }, [reqs]);

  // Effect to sync products state with parent component
  useEffect(() => {
    if (onProductsChange) {
      onProductsChange(products);
    }
  }, [products, onProductsChange]);

  const addProduct = () => {
    const newProducts = [
      ...products,
      { id: Date.now(), name: "", price: 0, _id: "", quantity: 0 },
    ];
    setProducts(newProducts);
  };

  const removeProduct = (itemId: number) => {
    if (products.length > 1) {
      const newProducts = products.filter((product) => product.id !== itemId);
      setProducts(newProducts);
    }
  };

  const updateProductField = (
    itemId: number,
    field: keyof Product,
    value: any
  ) => {
    const newProducts = products.map((item) =>
      item.id === itemId
        ? { 
            ...item, 
            [field]: (field === "quantity" || field === "price") ? Number(value) : value 
          }
        : item
    );
    setProducts(newProducts);
  };

  // Enhanced function to handle product selection with all details
  const handleProductSelection = (itemId: number, productId: string) => {
    if (!productId) {
      // Clear the product if no selection
      const newProducts = products.map((item) =>
        item.id === itemId
          ? {
              ...item,
              _id: "",
              name: "",
              quantity: 0,
              price: 0,
            }
          : item
      );
      setProducts(newProducts);
      return;
    }

    const selectedProduct = productMap[productId];
    if (selectedProduct) {
      // Update all relevant fields for the product
      const newProducts = products.map((item) =>
        item.id === itemId
          ? {
              ...item,
              _id: productId,
              name: selectedProduct.name,
              quantity: selectedProduct.quantity || 1, // Use requisition quantity or default to 1
              price: selectedProduct.price || 0, // Use found price or default to 0
            }
          : item
      );
      setProducts(newProducts);
    } else {
      // Fallback: try to get details from product catalog if available
      if (productCatalog && productCatalog[productId]) {
        const catalogItem = productCatalog[productId];
        const newProducts = products.map((item) =>
          item.id === itemId
            ? {
                ...item,
                _id: productId,
                name: catalogItem.name,
                quantity: 1, // Default quantity
                price: catalogItem.price,
              }
            : item
        );
        setProducts(newProducts);
      } else {
        // Just update the ID if no product details are found
        updateProductField(itemId, "_id", productId);
      }
    }
  };

  return (
    <div className="space-y-4">
      {products.map((product, index) => (
        <div key={product.id} className="grid grid-cols-12 gap-4 items-center">
          <FIlterSelect
            name={`product_id_${product.id}`}
            options={RequisitionOptions}
            className="w-full col-span-2"
            label="Item Name"
            onChange={(selectedValue) => {
              handleProductSelection(product.id, selectedValue);
            }}
            value={product._id}
          />

          <FIlterSelect
            name="variant"
            options={[
              { value: "high", label: "High" },
              { value: "low", label: "Low" },
            ]}
            value={reqs?.urgencyLevel || ""}
            className="w-full col-span-2"
            label="Variant"
          />

          <Input
            value={product.quantity}
            className="w-full col-span-2"
            label={"Quantity"}
            type="number"
            onChange={(e) =>
              updateProductField(product.id, "quantity", e.target.value)
            }
          />

          <Input
            value={product.price}
            className="w-full col-span-2"
            label={"Unit Price"}
            placeholder="Enter price"
            type="number"
            onChange={(e) =>
              updateProductField(product.id, "price", e.target.value)
            }
          />

          <Input
            value={product.price * product.quantity}
            className="w-full col-span-2"
            label={"Sub Total"}
            placeholder="Amount"
            type="number"
            readOnly
          />

          <div className="col-span-1 flex justify-end pt-1">
            {products.length > 1 && (
              <button
                type="button"
                onClick={() => removeProduct(product.id)}
                className="text-red-500 hover:text-red-700"
              >
                <Icon name="trashSimple" />
              </button>
            )}
          </div>
        </div>
      ))}

      <div
        className="flex justify-center bg-[#E8F2E8] rounded-full p-2 cursor-pointer"
        onClick={addProduct}
      >
        <button
          type="button"
          className="flex items-center justify-center gap-2 text-[#047401] font-medium"
        >
          <Plus size={16} />
          Add New Item
        </button>
      </div>
    </div>
  );
};

export default AddNewProduct;